import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'

interface InboxMessageContentProps {
  content: string
}

/**
 * Renders an Inbox message body as markdown — bold/italic, lists, and
 * clickable links (incl. bare URLs autolinked by remark-gfm). Staff previously
 * saw raw `**bold**` markers and non-clickable URLs (the bubble rendered
 * `message.content` as plain text).
 *
 * Messages are chat-style: single newlines are meaningful line breaks (e.g. the
 * deterministic bank-details block: Beneficiary / Bank / CLABE on separate
 * lines). CommonMark collapses isolated newlines, so we convert each ISOLATED
 * newline into a hard break (`  \n`) while leaving blank-line paragraph breaks
 * intact. This avoids pulling in `remark-breaks` as a dependency.
 */
export function InboxMessageContent({ content }: InboxMessageContentProps) {
  const withBreaks = content.replace(/(?<!\n)\n(?!\n)/g, '  \n')

  return (
    <div className="wrap-break-word">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline break-all"
            >
              {children}
            </a>
          ),
          p: ({ children }) => <p className="my-0.5 leading-relaxed">{children}</p>,
          ul: ({ children }) => <ul className="list-disc pl-5 my-1 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 my-1 space-y-0.5">{children}</ol>,
          code: ({ className, children }) =>
            className ? (
              <code className={className}>{children}</code>
            ) : (
              <code className="px-1 py-0.5 bg-foreground/10 rounded-sm text-xs font-mono">
                {children}
              </code>
            ),
          pre: ({ children }) => (
            <pre className="my-1.5 p-2 bg-foreground/10 rounded-sm overflow-x-auto text-xs font-mono">
              {children}
            </pre>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-current/30 pl-2 my-1 italic opacity-80">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-1.5">
              <table className="min-w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-current/20 px-2 py-1 text-left font-medium">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-current/20 px-2 py-1">{children}</td>
          ),
        }}
      >
        {withBreaks}
      </ReactMarkdown>
    </div>
  )
}
